import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, Reorder } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Plus, Trash2, Play, Music, GripVertical, ListMusic, Clock, X, Copy, LayoutTemplate
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SetlistManager = ({ projects = [], onSetlistsChange }) => {
  const navigate = useNavigate();
  const [setlists, setSetlists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedSetlist, setSelectedSetlist] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [templateName, setTemplateName] = useState("");
  
  // Form states
  const [newSetlist, setNewSetlist] = useState({ name: "", description: "", project_ids: [] });

  const fetchSetlists = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/setlists`);
      setSetlists(response.data);
      if (onSetlistsChange) onSetlistsChange(response.data);
    } catch (error) {
      console.error("Failed to load setlists", error);
    } finally {
      setLoading(false);
    }
  }, [onSetlistsChange]);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/setlist-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to load templates", error);
    }
  }, []);

  useEffect(() => {
    fetchSetlists();
    fetchTemplates();
  }, [fetchSetlists, fetchTemplates]);

  const handleCreateSetlist = async () => {
    if (!newSetlist.name.trim()) {
      toast.error("Please enter a setlist name");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/setlists`, newSetlist);
      setSetlists([response.data, ...setlists]);
      setCreateDialogOpen(false);
      setNewSetlist({ name: "", description: "", project_ids: [] });
      toast.success("Setlist created!");
    } catch (error) {
      toast.error("Failed to create setlist");
    }
  };

  const handleDuplicateSetlist = async (setlistId) => {
    try {
      const response = await axios.post(`${API}/setlists/${setlistId}/duplicate`);
      setSetlists([response.data, ...setlists]);
      toast.success("Setlist duplicated!");
    } catch (error) {
      toast.error("Failed to duplicate setlist");
    }
  };

  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate) {
      toast.error("Please select a template");
      return;
    }
    
    try {
      const params = templateName ? `?name=${encodeURIComponent(templateName)}` : '';
      const response = await axios.post(`${API}/setlists/from-template/${selectedTemplate}${params}`);
      setSetlists([response.data, ...setlists]);
      setTemplateDialogOpen(false);
      setSelectedTemplate("");
      setTemplateName("");
      toast.success("Setlist created from template!");
    } catch (error) {
      toast.error("Failed to create from template");
    }
  };

  const handleDeleteSetlist = async () => {
    if (!selectedSetlist) return;
    
    try {
      await axios.delete(`${API}/setlists/${selectedSetlist.id}`);
      setSetlists(setlists.filter(s => s.id !== selectedSetlist.id));
      toast.success("Setlist deleted");
    } catch (error) {
      toast.error("Failed to delete setlist");
    } finally {
      setDeleteDialogOpen(false);
      setSelectedSetlist(null);
    }
  };

  const handleReorderItems = async (setlistId, newItems) => {
    try {
      const items = newItems.map((item, index) => ({
        project_id: item.project_id,
        order: index
      }));
      
      await axios.put(`${API}/setlists/${setlistId}`, { items });
      fetchSetlists();
    } catch (error) {
      toast.error("Failed to reorder items");
    }
  };

  const handleRemoveFromSetlist = async (setlistId, projectId) => {
    try {
      await axios.delete(`${API}/setlists/${setlistId}/remove-project/${projectId}`);
      fetchSetlists();
      toast.success("Song removed from setlist");
    } catch (error) {
      toast.error("Failed to remove song");
    }
  };

  const handleAddToSetlist = async (setlistId, projectId) => {
    try {
      await axios.post(`${API}/setlists/${setlistId}/add-project?project_id=${projectId}`);
      fetchSetlists();
      toast.success("Song added to setlist");
    } catch (error) {
      toast.error("Failed to add song");
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || "Unknown Project";
  };

  const getProjectSlideCount = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.slides?.length || 0;
  };

  const toggleProjectSelection = (projectId) => {
    setNewSetlist(prev => ({
      ...prev,
      project_ids: prev.project_ids.includes(projectId)
        ? prev.project_ids.filter(id => id !== projectId)
        : [...prev.project_ids, projectId]
    }));
  };

  const songProjects = projects.filter(p => p.is_song);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
            <ListMusic className="w-6 h-6 text-amber-500" />
            Setlists
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Create setlists for seamless worship service transitions
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setTemplateDialogOpen(true)}
            className="gap-2"
            data-testid="template-setlist-btn"
          >
            <LayoutTemplate className="w-4 h-4" />
            From Template
          </Button>
          <Button 
            onClick={() => setCreateDialogOpen(true)}
            className="gap-2 bg-amber-500 hover:bg-amber-600 text-black"
            data-testid="create-setlist-btn"
          >
            <Plus className="w-4 h-4" />
            New Setlist
          </Button>
        </div>
      </div>

      {/* Setlists Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-6 bg-slate-200 dark:bg-slate-800 rounded w-3/4" />
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="h-4 bg-slate-200 dark:bg-slate-800 rounded" />
                  <div className="h-4 bg-slate-200 dark:bg-slate-800 rounded w-2/3" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : setlists.length === 0 ? (
        <Card className="p-10 text-center">
          <ListMusic className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold mb-2">No setlists yet</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Create a setlist to organize songs for your worship service.
          </p>
          <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            Create First Setlist
          </Button>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {setlists.map((setlist, index) => (
              <motion.div
                key={setlist.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card 
                  className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  data-testid={`setlist-card-${setlist.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <ListMusic className="w-5 h-5 text-amber-500" />
                        <CardTitle className="text-lg font-heading">{setlist.name}</CardTitle>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDuplicateSetlist(setlist.id)}
                          data-testid={`duplicate-setlist-${setlist.id}`}
                          title="Duplicate setlist"
                        >
                          <Copy className="w-4 h-4 text-muted-foreground" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedSetlist(setlist);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-setlist-${setlist.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                    {setlist.description && (
                      <CardDescription>{setlist.description}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    {/* Songs list */}
                    <div className="space-y-2 mb-4">
                      {setlist.items?.length > 0 ? (
                        <div className="space-y-1">
                          {setlist.items.slice(0, 4).map((item, idx) => (
                            <div 
                              key={item.project_id}
                              className="flex items-center gap-2 text-sm p-2 bg-slate-100 dark:bg-slate-800 rounded"
                            >
                              <span className="text-amber-500 font-semibold">{idx + 1}.</span>
                              <Music className="w-3 h-3 text-muted-foreground" />
                              <span className="truncate flex-1">{getProjectName(item.project_id)}</span>
                              <span className="text-xs text-muted-foreground">
                                {getProjectSlideCount(item.project_id)} slides
                              </span>
                            </div>
                          ))}
                          {setlist.items.length > 4 && (
                            <p className="text-xs text-muted-foreground text-center">
                              +{setlist.items.length - 4} more songs
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No songs added yet
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => {
                          setSelectedSetlist(setlist);
                          setEditDialogOpen(true);
                        }}
                        data-testid={`edit-setlist-${setlist.id}`}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 gap-1 bg-amber-500 hover:bg-amber-600 text-black"
                        onClick={() => navigate(`/setlist/${setlist.id}`)}
                        disabled={!setlist.items?.length}
                        data-testid={`present-setlist-${setlist.id}`}
                      >
                        <Play className="w-4 h-4" />
                        Present
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Create Setlist Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ListMusic className="w-5 h-5 text-amber-500" />
              Create New Setlist
            </DialogTitle>
            <DialogDescription>
              Create a setlist for your worship service and add songs.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="setlist-name">Setlist Name</Label>
              <Input
                id="setlist-name"
                placeholder="Sunday Morning Worship"
                value={newSetlist.name}
                onChange={(e) => setNewSetlist({ ...newSetlist, name: e.target.value })}
                data-testid="setlist-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="setlist-desc">Description (optional)</Label>
              <Textarea
                id="setlist-desc"
                placeholder="Add a description..."
                value={newSetlist.description}
                onChange={(e) => setNewSetlist({ ...newSetlist, description: e.target.value })}
                data-testid="setlist-desc-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Select Songs</Label>
              <ScrollArea className="h-[200px] border rounded-lg p-3">
                {songProjects.length > 0 ? (
                  <div className="space-y-2">
                    {songProjects.map((project) => (
                      <div
                        key={project.id}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          newSetlist.project_ids.includes(project.id)
                            ? 'bg-amber-100 dark:bg-amber-900/20'
                            : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                        onClick={() => toggleProjectSelection(project.id)}
                      >
                        <Checkbox
                          checked={newSetlist.project_ids.includes(project.id)}
                          onCheckedChange={() => toggleProjectSelection(project.id)}
                        />
                        <Music className="w-4 h-4 text-amber-500" />
                        <span className="flex-1">{project.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {project.slides?.length || 0} slides
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No song projects yet. Create a song project first.
                  </p>
                )}
              </ScrollArea>
              {newSetlist.project_ids.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  {newSetlist.project_ids.length} song(s) selected
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateSetlist} 
              className="bg-amber-500 hover:bg-amber-600 text-black"
              data-testid="confirm-create-setlist"
            >
              Create Setlist
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Setlist Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ListMusic className="w-5 h-5 text-amber-500" />
              Edit Setlist: {selectedSetlist?.name}
            </DialogTitle>
            <DialogDescription>
              Drag to reorder songs or remove them from the setlist.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current songs in setlist */}
            <div className="space-y-2">
              <Label>Songs in Setlist</Label>
              {selectedSetlist?.items?.length > 0 ? (
                <Reorder.Group
                  axis="y"
                  values={selectedSetlist.items}
                  onReorder={(newOrder) => {
                    setSelectedSetlist({ ...selectedSetlist, items: newOrder });
                    handleReorderItems(selectedSetlist.id, newOrder);
                  }}
                  className="space-y-2"
                >
                  {selectedSetlist.items.map((item, idx) => (
                    <Reorder.Item key={item.project_id} value={item}>
                      <div className="flex items-center gap-2 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg group">
                        <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
                        <span className="text-amber-500 font-bold w-6">{idx + 1}</span>
                        <Music className="w-4 h-4 text-muted-foreground" />
                        <span className="flex-1 font-medium">{getProjectName(item.project_id)}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleRemoveFromSetlist(selectedSetlist.id, item.project_id)}
                        >
                          <X className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </Reorder.Item>
                  ))}
                </Reorder.Group>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No songs in this setlist
                </p>
              )}
            </div>

            {/* Add more songs */}
            <div className="space-y-2">
              <Label>Add More Songs</Label>
              <ScrollArea className="h-[150px] border rounded-lg p-3">
                {songProjects
                  .filter(p => !selectedSetlist?.items?.some(i => i.project_id === p.id))
                  .map((project) => (
                    <div
                      key={project.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
                      onClick={() => handleAddToSetlist(selectedSetlist.id, project.id)}
                    >
                      <Plus className="w-4 h-4 text-green-500" />
                      <Music className="w-4 h-4 text-amber-500" />
                      <span className="flex-1">{project.name}</span>
                    </div>
                  ))}
                {songProjects.filter(p => !selectedSetlist?.items?.some(i => i.project_id === p.id)).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    All songs are already in this setlist
                  </p>
                )}
              </ScrollArea>
            </div>

            {/* Auto-scroll speed */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Auto-Scroll Speed
              </Label>
              <div className="flex items-center gap-4">
                <Slider
                  value={[selectedSetlist?.auto_scroll_speed || 5]}
                  min={2}
                  max={15}
                  step={1}
                  onValueChange={async ([value]) => {
                    setSelectedSetlist({ ...selectedSetlist, auto_scroll_speed: value });
                    await axios.put(`${API}/setlists/${selectedSetlist.id}`, { auto_scroll_speed: value });
                  }}
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground w-12">
                  {selectedSetlist?.auto_scroll_speed || 5}s
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setEditDialogOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Setlist</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedSetlist?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteSetlist} data-testid="confirm-delete-setlist">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <LayoutTemplate className="w-5 h-5 text-amber-500" />
              Create from Template
            </DialogTitle>
            <DialogDescription>
              Start with a pre-configured setlist structure for your service type.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Template</Label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger data-testid="template-select">
                  <SelectValue placeholder="Choose a template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{template.name}</span>
                        <span className="text-xs text-muted-foreground">{template.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedTemplate && (
              <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-lg space-y-2">
                <p className="text-sm font-medium">Template Structure:</p>
                <div className="flex flex-wrap gap-2">
                  {templates.find(t => t.id === selectedTemplate)?.structure.map((item, idx) => (
                    <span key={idx} className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs rounded-full capitalize">
                      {idx + 1}. {item}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Suggested: {templates.find(t => t.id === selectedTemplate)?.suggested_count} songs
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="template-name">Custom Name (optional)</Label>
              <Input
                id="template-name"
                placeholder="Leave empty to use template name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                data-testid="template-name-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateFromTemplate}
              disabled={!selectedTemplate}
              className="bg-amber-500 hover:bg-amber-600 text-black"
              data-testid="confirm-template-create"
            >
              Create Setlist
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SetlistManager;
